import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';

type WeightEntry = {
  _id?: string;
  date: string; // ISO string
  weightKg: number;
  source?: string;
  note?: string;
};

// Chart point type including optional regression trend value
type ChartPoint = { timestamp: number; date: string; weight: number; trend?: number };

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

function formatDateLocal(dateIso: string) {
  const d = new Date(dateIso);
  return d.toLocaleDateString('en-GB');
}

const WeightPage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [weights, setWeights] = useState<WeightEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [weightKg, setWeightKg] = useState<string>('');
  const [note, setNote] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [importSummary, setImportSummary] = useState<string>('');
  // CSV preview state
  type ParsedEntry = { date: string; weightKg: number; note?: string; source?: string };
  const [previewEntries, setPreviewEntries] = useState<ParsedEntry[] | null>(null);
  const [previewErrors, setPreviewErrors] = useState<string[]>([]);

  const fetchWeights = async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/weights?limit=2000`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to load weights');
      const data = await res.json();
      setWeights(data);
    } catch (e: any) {
      setError(e?.message || 'Failed to load weights');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWeights();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const addWeight = async () => {
    setError('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/weights`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          date: new Date(date).toISOString(),
          weightKg: parseFloat(weightKg),
          note
        })
      });
      if (!res.ok) throw new Error('Failed to add weight');
      setWeightKg('');
      setNote('');
      await fetchWeights();
    } catch (e: any) {
      setError(e?.message || 'Failed to add weight');
    }
  };

  const deleteWeight = async (id?: string) => {
    if (!id) return;
    const token = localStorage.getItem('token');
    await fetch(`${API_BASE_URL}/weights/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` }
    });
    await fetchWeights();
  };

  // Regression-based trend (over last 8 weeks, fallback to all)
  const sorted = useMemo(() => [...weights].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()), [weights]);
  const trendData = useMemo<ChartPoint[]>(() => {
    if (!sorted.length) return [] as ChartPoint[];
    const base: ChartPoint[] = sorted.map(w => ({ timestamp: new Date(w.date).getTime(), date: w.date, weight: w.weightKg }));
    const lastWindowMs = 56 * 24 * 60 * 60 * 1000;
    const lastTs = base[base.length - 1].timestamp;
    const windowStart = lastTs - lastWindowMs;
    const pts = (base.filter(p => p.timestamp >= windowStart).length >= 3 ? base.filter(p => p.timestamp >= windowStart) : base);
    if (pts.length >= 2) {
      const weekMs = 7 * 24 * 60 * 60 * 1000;
      const t0 = pts[0].timestamp / weekMs;
      const xs = pts.map(p => p.timestamp / weekMs - t0);
      const ys = pts.map(p => p.weight);
      const n = xs.length;
      const sumX = xs.reduce((a, b) => a + b, 0);
      const sumY = ys.reduce((a, b) => a + b, 0);
      const sumXX = xs.reduce((a, b) => a + b * b, 0);
      const sumXY = xs.reduce((acc, x, i) => acc + x * ys[i], 0);
      const denom = n * sumXX - sumX * sumX;
      const slope = denom !== 0 ? (n * sumXY - sumX * sumY) / denom : undefined;
      const intercept = denom !== 0 ? (sumY - (slope as number) * sumX) / n : undefined;
      return base.map(p => ({ ...p, trend: slope !== undefined && intercept !== undefined ? intercept + slope * (p.timestamp / weekMs - t0) : undefined }));
    }
    // Ensure the returned shape always includes the 'trend' key (optional)
    return base.map(p => ({ ...p, trend: undefined }));
  }, [sorted]);

  // Build chart data
  const chartData = useMemo<ChartPoint[]>(() => trendData, [trendData]);

  // X ticks every 7 days
  const xTicks: number[] = useMemo(() => {
    if (chartData.length === 0) return [];
    const minTs = chartData[0].timestamp;
    const maxTs = chartData[chartData.length - 1].timestamp;
    const ticks: number[] = [];
    let t = minTs;
    const week = 7 * 24 * 60 * 60 * 1000;
    while (t <= maxTs) {
      ticks.push(t);
      t += week;
    }
    return ticks;
  }, [chartData]);

  const yDomain = useMemo<[number, number]>(() => {
    if (chartData.length === 0) return [0, 1];
    const vals = chartData.flatMap(d => [d.weight, d.trend ?? d.weight]);
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    const pad = Math.max(0.5, (max - min) * 0.1);
    return [Math.floor(min - pad), Math.ceil(max + pad)];
  }, [chartData]);

  const TooltipContent = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0]?.payload;
      return (
        <div style={{
          backgroundColor: 'var(--background-light)',
          padding: '12px',
          border: '2px solid var(--primary-color)',
          borderRadius: '10px',
          boxShadow: '0 6px 12px var(--shadow-color)'
        }}>
          <div style={{ fontWeight: 700, color: 'var(--text-primary)', marginBottom: 6 }}>
            {formatDateLocal(data.date)}
          </div>
          {payload.find((p: any) => p.dataKey === 'weight') && (
            <div style={{ color: 'var(--primary-color)' }}>Weight: <b>{data.weight.toFixed(1)} kg</b></div>
          )}
          {payload.find((p: any) => p.dataKey === 'trend') && (
            <div style={{ color: 'var(--accent-color)' }}>Trend: <b>{(data.trend || 0).toFixed(1)} kg</b></div>
          )}
        </div>
      );
    }
    return null;
  };

  const onCsvSelect = async (file: File) => {
    setImportSummary('');
    setPreviewErrors([]);
    const text = await file.text();
    const errors: string[] = [];
    // Simple CSV parsing (no quoted-field handling). Expect a header row.
    const lines = text.split(/\r?\n/).filter(l => l.trim().length > 0);
    if (lines.length < 2) {
      setPreviewEntries(null);
      setPreviewErrors(['No data found in CSV. Include a header row and at least one data row.']);
      return;
    }
    const rawHeader = lines[0].split(',');
    const normalized = rawHeader.map(h => h.trim());
    const lower = normalized.map(h => h.toLowerCase());
    const findIdx = (cands: string[]) => lower.findIndex(h => cands.includes(h));
    const dateIdx = findIdx(['date', 'start', 'start date', 'timestamp']);
    const unitIdx = findIdx(['unit', 'units']);
    const weightIdx = findIdx(['weightkg', 'weight', 'value', 'body weight', 'weight (kg)', 'weight (lb)', 'weight (lbs)']);
    const noteIdx = findIdx(['note', 'notes', 'comment']);
    const sourceIdx = findIdx(['source', 'source name', 'device', 'origin']);

    if (dateIdx === -1 || weightIdx === -1) {
      setPreviewEntries(null);
      setPreviewErrors(['Missing required headers. Include date and a weight column (e.g., weightKg, weight, or Value).']);
      return;
    }

    const weightHeader = lower[weightIdx];
    const headerImpliesLb = /lb/.test(weightHeader) && !/kg/.test(weightHeader);

    const preview: ParsedEntry[] = [];
    for (let i = 1; i < lines.length; i++) {
      const cols = lines[i].split(',');
      if (!cols[dateIdx] || !cols[weightIdx]) continue;
      const d = new Date(cols[dateIdx]);
      const rawVal = String(cols[weightIdx]).replace(/[^0-9.+-eE]/g, '');
      const valNum = parseFloat(rawVal);
      if (!Number.isFinite(d.getTime())) {
        errors.push(`Row ${i + 1}: invalid date '${cols[dateIdx]}'`);
        continue;
      }
      if (!Number.isFinite(valNum)) {
        errors.push(`Row ${i + 1}: invalid weight '${cols[weightIdx]}'`);
        continue;
      }

      // Determine per-row unit
      let isLb = false;
      if (unitIdx >= 0 && cols[unitIdx]) {
        const u = String(cols[unitIdx]).trim().toLowerCase();
        if (u.includes('lb')) isLb = true;
        if (u.includes('kg')) isLb = false;
      } else {
        isLb = headerImpliesLb;
      }

      const weightKgVal = isLb ? valNum * 0.45359237 : valNum;
      const noteVal = noteIdx >= 0 ? cols[noteIdx] : '';
      const sourceVal = sourceIdx >= 0 ? cols[sourceIdx] : 'import';
      preview.push({ date: d.toISOString(), weightKg: weightKgVal, note: noteVal, source: sourceVal });
    }
    setPreviewEntries(preview);
    setPreviewErrors(errors);
  };

  const confirmImport = async () => {
    if (!previewEntries || previewEntries.length === 0) return;
    setImportSummary('');
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_BASE_URL}/weights/bulk`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ entries: previewEntries })
    });
    if (res.ok) {
      const data = await res.json();
      const msgParts: string[] = [];
      if (typeof data.upserted === 'number') msgParts.push(`${data.upserted} inserted`);
      if (typeof data.modified === 'number') msgParts.push(`${data.modified} updated`);
      if (msgParts.length === 0 && typeof data.processed === 'number') msgParts.push(`${data.processed} processed`);
      setImportSummary(`Import successful: ${msgParts.join(', ')}.`);
      setPreviewEntries(null);
      setPreviewErrors([]);
      await fetchWeights();
    } else {
      setImportSummary('Import failed. Please check your file format.');
    }
  };

  const discardPreview = () => {
    setPreviewEntries(null);
    setPreviewErrors([]);
    setImportSummary('');
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <p className="text-gray-700 dark:text-gray-200">Please log in to track your weight.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">Weight Tracking</h1>
          <p className="text-gray-600 dark:text-gray-400">Log your weight and import historical data via CSV.</p>
        </div>

        {/* Add Form */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6 border border-gray-200 dark:border-gray-700">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date</label>
              <input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-full border rounded px-3 py-2 dark:bg-gray-700 dark:text-white" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Weight (kg)</label>
              <input type="number" min="0" step="0.1" value={weightKg} onChange={e => setWeightKg(e.target.value)} className="w-full border rounded px-3 py-2 dark:bg-gray-700 dark:text-white" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Note (optional)</label>
              <input type="text" value={note} onChange={e => setNote(e.target.value)} className="w-full border rounded px-3 py-2 dark:bg-gray-700 dark:text-white" />
            </div>
          </div>
          <div className="mt-4">
            <button onClick={addWeight} disabled={!weightKg} className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded disabled:opacity-50">Add</button>
          </div>
          {error && <div className="mt-2 text-red-600 text-sm">{error}</div>}
        </div>

        {/* CSV Import */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6 border border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Bulk Import via CSV</h2>
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-3 space-y-1">
            <p>
              Accepted headers (auto-mapped): <code>date</code> or <code>start</code>,
              <code className="ml-1">weightKg</code> or <code>value</code> or <code>weight</code>, optional <code className="ml-1">unit</code> (<code>kg</code> or <code>lb</code>), optional <code className="ml-1">note</code>, optional <code className="ml-1">source</code>.
            </p>
            <p>Units: If a row or header indicates pounds (<code>lb</code>/<code>lbs</code>), values are converted to kilograms automatically.</p>
            <p>Dates: Use <code>YYYY-MM-DD</code> or an ISO timestamp (<code>YYYY-MM-DDTHH:mm:ssZ</code>).</p>
            <p>Example supported export: <code>Date,Value,Unit,Source</code> (Apple Health).</p>
          </div>
          <input type="file" accept=".csv,text/csv" onChange={e => e.target.files && e.target.files[0] && onCsvSelect(e.target.files[0])} />

          {/* Preview */}
          {previewEntries && (
            <div className="mt-4">
              <div className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                Previewing {previewEntries.length} rows{previewErrors.length ? `, ${previewErrors.length} issues found` : ''}.
              </div>
              <div className="max-h-64 overflow-auto border border-gray-200 dark:border-gray-700 rounded">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-600 dark:text-gray-300">
                      <th className="py-2 px-3">Date</th>
                      <th className="py-2 px-3">Weight (kg)</th>
                      <th className="py-2 px-3">Note</th>
                      <th className="py-2 px-3">Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewEntries.slice(0, 50).map((r, idx) => (
                      <tr key={idx} className="border-t border-gray-100 dark:border-gray-700">
                        <td className="py-2 px-3">{formatDateLocal(r.date)}</td>
                        <td className="py-2 px-3">{r.weightKg.toFixed(2)}</td>
                        <td className="py-2 px-3">{r.note || ''}</td>
                        <td className="py-2 px-3">{r.source || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {previewErrors.length > 0 && (
                <div className="mt-2 text-xs text-amber-700 dark:text-amber-400">
                  {previewErrors.slice(0, 5).map((e, i) => <div key={i}>• {e}</div>)}
                  {previewErrors.length > 5 && <div>… {previewErrors.length - 5} more</div>}
                </div>
              )}
              <div className="mt-3 flex gap-2">
                <button onClick={confirmImport} disabled={!previewEntries.length} className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded disabled:opacity-50">Import {previewEntries.length} rows</button>
                <button onClick={discardPreview} className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded">Discard</button>
              </div>
            </div>
          )}

          {importSummary && <div className="mt-3 text-sm text-gray-700 dark:text-gray-300">{importSummary}</div>}
        </div>

        {/* Chart */}
        {chartData.length > 1 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6 border border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">Weight Trend</h2>
            <div style={{ width: '100%', height: 380 }}>
              <ResponsiveContainer>
                <LineChart data={chartData} margin={{ top: 20, right: 24, left: 48, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                  <XAxis
                    type="number"
                    dataKey="timestamp"
                    ticks={xTicks}
                    domain={[chartData[0].timestamp, chartData[chartData.length - 1].timestamp]}
                    tickFormatter={(t) => new Date(t).toLocaleDateString('en-GB')}
                    stroke="var(--text-secondary)"
                  />
                  <YAxis
                    label={{ value: 'Weight (kg)', angle: -90, position: 'insideLeft', offset: -20, style: { textAnchor: 'middle', fill: 'var(--text-secondary)' } }}
                    domain={yDomain as any}
                    stroke="var(--text-secondary)"
                  />
                  <Tooltip content={<TooltipContent />} />
                  <Legend />
                  <Line type="monotone" dataKey="weight" name="Weight (kg)" stroke="var(--primary-color)" dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Simple Table */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">Recent Entries</h2>
          {loading ? (
            <div className="text-gray-600 dark:text-gray-300">Loading...</div>
          ) : weights.length === 0 ? (
            <div className="text-gray-600 dark:text-gray-300">No entries yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-600 dark:text-gray-300">
                    <th className="py-2 pr-4">Date</th>
                    <th className="py-2 pr-4">Weight (kg)</th>
                    <th className="py-2 pr-4">Note</th>
                    <th className="py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {[...weights].reverse().slice(0, 50).map(w => (
                    <tr key={w._id || w.date} className="border-t border-gray-100 dark:border-gray-700">
                      <td className="py-2 pr-4">{formatDateLocal(w.date)}</td>
                      <td className="py-2 pr-4">{w.weightKg.toFixed(1)}</td>
                      <td className="py-2 pr-4">{w.note || ''}</td>
                      <td className="py-2">
                        {w._id && (
                          <button onClick={() => deleteWeight(w._id)} className="text-red-600 hover:underline">Delete</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Trend Preview */}
        {chartData.length > 1 && (
          <div className="mt-6 p-4 text-sm text-gray-600 dark:text-gray-300">
            <div>
              Showing {chartData.length} points. Latest weight: <b>{chartData[chartData.length - 1].weight.toFixed(1)} kg</b>. Trend: <b>{(chartData[chartData.length - 1].trend || chartData[chartData.length - 1].weight).toFixed(1)} kg</b>.
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WeightPage;


